import { trackLegacyEvent } from "@analytics";
import { Body, Button, Heading } from "@biom3/react";
import { useEffect, useMemo, useState } from "react";

import { appConfig } from "@/constants";

import { RewardBaseModal } from "../RewardBaseModal/RewardBaseModal";
import type { BonusDetails } from "./types";

const standardRotatingGem = `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/videos/immutable/rotating-gem--standard.mp4`;
const standardRotatingGemWebm = `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/videos/immutable/rotating-gem--standard.webm`;

export type GenericBonusModalState =
  | undefined
  | "login"
  | "success"
  | "already_claimed";

interface GenericBonusModalProps {
  state: GenericBonusModalState;
  onClose: () => void;
  onLogin: () => void;
  bonusDetails: BonusDetails | null;
}

export const GenericBonusModal = ({
  state,
  onClose,
  onLogin,
  bonusDetails,
}: GenericBonusModalProps) => {
  const [videoOpacity, setVideoOpacity] = useState(0);

  const modalContent = useMemo(() => {
    switch (state) {
      case "success":
        return bonusDetails?.successState;
      case "already_claimed":
        return bonusDetails?.alreadyClaimedState;
      default:
        return bonusDetails?.defaultState;
    }
  }, [state, bonusDetails]);

  const screen = useMemo(() => {
    return state === "success"
      ? "GemBonusSuccess"
      : state === "already_claimed"
        ? "GemBonusAlreadyClaimed"
        : "GemBonusLogin";
  }, [state]);

  useEffect(() => {
    trackLegacyEvent({
      screen,
      action: "Viewed",
      control: "Screen",
      controlType: "Effect",
      userJourney: "Claim",
      extras: {
        code: bonusDetails?.code,
      },
    });
  }, [screen, bonusDetails?.code]);

  const close = () => {
    trackLegacyEvent({
      screen,
      action: "Closed",
      control: "Button",
      controlType: "Button",
      userJourney: "Claim",
    });
    onClose();
  };

  const handleCta = () => {
    if (state === "login") {
      trackLegacyEvent({
        screen,
        action: "Pressed",
        control: "Button",
        controlType: "Button",
        userJourney: "Claim",
        extras: {
          code: bonusDetails?.code,
        },
      });
      onLogin();
    }
    close();
  };

  return (
    <RewardBaseModal visible={true} close={close}>
      <video
        style={{
          alignSelf: "center",
          width: "160px",
          aspectRatio: 1,
          opacity: videoOpacity,
          transition: "opacity 1000ms ease-in-out",
        }}
        autoPlay
        preload="auto"
        loop
        muted
        controls={false}
        onLoadedData={() => setVideoOpacity(1)}
      >
        <source src={standardRotatingGem} type="video/mp4" />
        <source src={standardRotatingGemWebm} type="video/webm" />
      </video>

      <Heading
        size="medium"
        sx={{
          px: "base.spacing.x3",
          textAlign: "center",
          lineHeight: "1.2",
        }}
      >
        {modalContent?.heading}
      </Heading>
      <Body
        size="small"
        sx={{
          color: "base.color.text.body.secondary",
          textAlign: "center",
        }}
      >
        {modalContent?.body}
      </Body>
      <Button
        size="large"
        variant="primary"
        onClick={handleCta}
        sx={{ mt: "auto" }}
      >
        {modalContent?.buttonText}
      </Button>
    </RewardBaseModal>
  );
};
